import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { 
  MapPin, Navigation, Clock, CheckCircle, AlertCircle, 
  User, Phone, Loader2, RefreshCw, Home 
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { roadsideAPI } from '../../services/roadsideAPI';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';

// Custom icons for map markers
const customerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const providerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

export const RequestTracking = ({ requestNumber }) => {
  const { t } = useTranslation();
  const [requestData, setRequestData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const navigate = useNavigate();

  const getStatusConfig = (status) => {
    const configs = {
      'Searching': {
        icon: Loader2,
        color: 'bg-blue-100 text-blue-700 border-blue-300',
        iconColor: 'text-blue-600',
        message: t('roadside.status.searchingMsg'),
        animate: true
      },
      'Assigned': {
        icon: CheckCircle,
        color: 'bg-green-100 text-green-700 border-green-300',
        iconColor: 'text-green-600',
        message: t('roadside.status.assignedMsg')
      },
      'En Route': {
        icon: Navigation,
        color: 'bg-purple-100 text-purple-700 border-purple-300',
        iconColor: 'text-purple-600',
        message: t('roadside.status.enRouteMsg'),
        showETA: true
      },
      'Arrived': {
        icon: MapPin,
        color: 'bg-orange-100 text-orange-700 border-orange-300',
        iconColor: 'text-orange-600',
        message: t('roadside.status.arrivedMsg')
      },
      'In Progress': {
        icon: User,
        color: 'bg-indigo-100 text-indigo-700 border-indigo-300',
        iconColor: 'text-indigo-600',
        message: t('roadside.status.inProgressMsg')
      },
      'Completed': {
        icon: CheckCircle,
        color: 'bg-green-100 text-green-700 border-green-300',
        iconColor: 'text-green-600',
        message: t('roadside.status.completedMsg'),
        final: true
      }
    };
    return configs[status] || configs['Searching'];
  };

  useEffect(() => {
    if (requestNumber) {
      fetchRequestStatus();
      
      // Poll every 5 seconds
      const interval = setInterval(fetchRequestStatus, 5000);
      
      return () => clearInterval(interval);
    }
  }, [requestNumber]);

  const fetchRequestStatus = async () => {
    try {
      const data = await roadsideAPI.getRequestStatus(requestNumber);
      setRequestData(data);
      setLastUpdate(new Date());
      setIsLoading(false);
      setError('');

      // Show toast for status changes
      if (requestData && data.status !== requestData.status) {
        const config = getStatusConfig(data.status);
        if (config) {
          toast.info(config.message);
        }
      }
    } catch (err) {
      console.error('Error fetching status:', err);
      if (isLoading) {
        setError(t('roadside.errors.loadFailed'));
        setIsLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    setIsLoading(true);
    fetchRequestStatus();
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (isLoading && !requestData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <Loader2 className="w-12 h-12 text-red-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">{t('common.loading')}</p>
        </Card>
      </div>
    );
  }

  if (error && !requestData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50 flex items-center justify-center px-4">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('roadside.errors.loadFailed')}</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={handleRefresh} className="bg-red-600 hover:bg-red-700">
            {t('common.loading')}
          </Button>
        </Card>
      </div>
    );
  }

  const config = getStatusConfig(requestData?.status);
  const StatusIcon = config.icon;
  const isCompleted = requestData?.status === 'Completed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50 pb-safe">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('roadside.step5.title', { number: requestData?.request_number })}</h1>
            <p className="text-sm text-gray-600">{t('roadside.step5.liveTracking')}</p>
          </div>
          <Button
            onClick={handleRefresh}
            variant="outline"
            size="sm"
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Status Card */}
        <Card className={`p-6 border-2 ${config.color}`}>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
              <StatusIcon className={`w-8 h-8 ${config.iconColor} ${config.animate ? 'animate-spin' : ''}`} />
            </div>
            <div className="flex-1">
              <Badge className={`${config.color} border mb-2`}>{t(`roadside.status.${requestData?.status?.toLowerCase().replace(' ', '')}`) || requestData?.status}</Badge>
              <p className="font-semibold text-gray-900 text-lg">{config.message}</p>
              {requestData?.estimated_arrival && config.showETA && (
                <div className="flex items-center gap-2 mt-2 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{t('roadside.status.eta', { time: requestData.estimated_arrival })}</span>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Flagged Warning */}
        {requestData?.flagged && (
          <Card className="p-6 bg-yellow-50 border-2 border-yellow-300">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-yellow-900 mb-1">{t('roadside.status.needsDispatch')}</p>
                <p className="text-sm text-yellow-800">
                  {t('roadside.status.needsDispatchMsg')}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Map */}
        <Card className="p-0 overflow-hidden">
          <div className="h-72 sm:h-96">
            <MapContainer
              center={[
                requestData?.customer_location?.latitude || 0,
                requestData?.customer_location?.longitude || 0
              ]}
              zoom={14}
              style={{ height: '100%', width: '100%' }}
              zoomControl={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* Customer Location */}
              {requestData?.customer_location && (
                <Marker
                  position={[
                    requestData.customer_location.latitude,
                    requestData.customer_location.longitude
                  ]}
                  icon={customerIcon}
                >
                  <Popup>Your Location</Popup>
                </Marker>
              )}

              {/* Provider Location */}
              {requestData?.provider_location && (
                <Marker
                  position={[
                    requestData.provider_location.latitude,
                    requestData.provider_location.longitude
                  ]}
                  icon={providerIcon}
                >
                  <Popup>Mechanic Location</Popup>
                </Marker>
              )}
            </MapContainer>
          </div>
        </Card>

        {/* Request Details */}
        <Card className="p-6">
          <h3 className="font-bold text-gray-900 mb-4">{t('roadside.step5.serviceDetails')}</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('roadside.step4.vehicle')}:</span>
              <span className="font-semibold text-gray-900">
                {requestData?.vehicle_year} {requestData?.vehicle_make} {requestData?.vehicle_model}
              </span>
            </div>
            {requestData?.tire_size && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('roadside.step4.tireSize')}</span>
                <span className="font-semibold text-gray-900 font-mono tire-size">{requestData.tire_size}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">{t('roadside.step4.serviceType')}:</span>
              <span className="font-semibold text-gray-900">{requestData?.service_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('roadside.step4.location')}:</span>
              <span className="font-medium text-gray-900 text-right max-w-xs">
                {requestData?.customer_location?.address}
              </span>
            </div>
            {requestData?.after_hours && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('roadside.step4.callOutFee')}</span>
                <span className="font-semibold text-gray-900 numeric">${requestData?.call_out_fee?.toFixed(2)}</span>
              </div>
            )}
            {requestData?.provider_name && (
              <div className="flex justify-between">
                <span className="text-gray-600">Mechanic:</span>
                <span className="font-semibold text-gray-900">{requestData.provider_name}</span>
              </div>
            )}
          </div>
        </Card>

        {/* Last Update */}
        <div className="text-center text-xs text-gray-500">
          <p>{t('roadside.step5.lastUpdate', { time: lastUpdate.toLocaleTimeString() })}</p>
          <p className="mt-1">{t('roadside.step5.autoRefresh')}</p>
        </div>

        {/* Completed - Go Home */}
        {isCompleted && (
          <Button
            onClick={handleGoHome}
            size="lg"
            className="w-full bg-green-600 hover:bg-green-700 text-white py-6"
          >
            <Home className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0" />
            {t('roadside.step5.returnHome')}
          </Button>
        )}

        {/* Emergency Contact */}
        {!isCompleted && (
          <Card className="p-6 bg-gray-50">
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-gray-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-1">{t('roadside.step5.needHelp')}</p>
                <a
                  href="tel:3057724150"
                  className="text-red-600 font-semibold hover:text-red-700 phone-number"
                >
                  {t('roadside.step5.callUs')}
                </a>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
